/**
 * RewardBurst — global "splash" toast for v0.10.0 emotional rewards.
 *
 * Dispatch: `window.dispatchEvent(new CustomEvent('reward:burst', { detail }))`
 *
 *   detail = {
 *     kind: 'coin' | 'bond' | 'level_up' | 'wish_fulfilled',
 *     amount?: number,         // for 'coin' / 'bond'
 *     title_zh?: string,       // for 'level_up' / 'wish_fulfilled'
 *     title_en?: string,
 *     unlock?: string | null,  // for 'level_up'
 *     petName?: string,        // for 'wish_fulfilled'
 *   }
 *
 * Mount once near the app root (AppShell). Multiple bursts queue and animate
 * in sequence — never overlap. Auto-dismiss in 2.4s (or 3.6s for level_up).
 */
import { useEffect, useRef, useState } from 'react';
import { useLang, t } from '../i18n';
import { playCue } from '../lib/gameAudio';

interface BurstDetail {
  kind: 'coin' | 'bond' | 'level_up' | 'wish_fulfilled';
  amount?: number;
  title_zh?: string;
  title_en?: string;
  unlock?: string | null;
  petName?: string;
}

interface QueueItem extends BurstDetail {
  id: number;
}

export function fireBurst(detail: BurstDetail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('reward:burst', { detail }));
}

let nextId = 1;

export default function RewardBurst() {
  const lang = useLang();
  const [active, setActive] = useState<QueueItem | null>(null);
  const queueRef = useRef<QueueItem[]>([]);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    function handler(e: Event) {
      const detail = (e as CustomEvent<BurstDetail>).detail;
      if (!detail || typeof detail !== 'object') return;
      const item: QueueItem = { id: nextId++, ...detail };
      queueRef.current.push(item);
      playCue(detail.kind === 'coin' || detail.kind === 'bond' ? 'success' : 'message');
      pump();
    }
    function pump() {
      if (active || queueRef.current.length === 0) return;
      const next = queueRef.current.shift()!;
      setActive(next);
      const ttl = next.kind === 'level_up' ? 3600 : 2400;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setActive(null);
        // tail-recurse via microtask so React commits null first
        setTimeout(pump, 30);
      }, ttl);
    }
    window.addEventListener('reward:burst', handler as EventListener);
    return () => {
      window.removeEventListener('reward:burst', handler as EventListener);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active) return null;

  if (active.kind === 'level_up') {
    const title = lang === 'en' ? active.title_en : active.title_zh;
    return (
      <div style={overlayStyle}>
        <div style={levelUpCardStyle}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#b45309', letterSpacing: 2 }}>
            {t('rewardBurst.levelUp.eyebrow')}
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: '#7c2d12', margin: '6px 0' }}>
            💗 Bond Lv.{active.amount}
          </div>
          {title && (
            <div style={{ fontSize: 22, fontWeight: 800, color: '#9a3412' }}>
              {title}
            </div>
          )}
          {active.unlock && (
            <div style={{ marginTop: 10, fontSize: 14, color: '#7c2d12' }}>
              {t('rewardBurst.levelUp.unlock')} <strong>{active.unlock}</strong>
            </div>
          )}
        </div>
        <Sparks />
      </div>
    );
  }

  if (active.kind === 'wish_fulfilled') {
    const title = lang === 'en' ? active.title_en : active.title_zh;
    return (
      <div style={overlayStyle}>
        <div style={wishCardStyle}>
          <div style={{ fontSize: 38 }}>💗</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#7c2d12', marginTop: 4 }}>
            {t('rewardBurst.wish.title', { name: active.petName || '' })}
          </div>
          {title && (
            <div style={{ fontSize: 14, color: '#9a3412', marginTop: 4, fontStyle: 'italic' }}>
              「{title}」
            </div>
          )}
          {typeof active.amount === 'number' && (
            <div style={{ fontSize: 14, color: '#b45309', marginTop: 6 }}>
              +{active.amount} {t('rewardBurst.bondShort')}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (active.kind === 'bond') {
    return (
      <div style={cornerToastStyle('#fce7f3', '#9d174d')}>
        💗 +{active.amount} {t('rewardBurst.bondShort')}
      </div>
    );
  }

  // coin
  return (
    <div style={cornerToastStyle('#fef3c7', '#92400e')}>
      💰 +{active.amount}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  pointerEvents: 'none', zIndex: 9999,
};

const levelUpCardStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fff7ed 0%, #fde68a 100%)',
  borderRadius: 24, padding: '28px 36px',
  boxShadow: '0 14px 40px rgba(180, 83, 9, 0.35)',
  textAlign: 'center', minWidth: 260,
  animation: 'pop 360ms cubic-bezier(.2,.9,.3,1.4)',
};

const wishCardStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #fff1f2 0%, #fbcfe8 100%)',
  borderRadius: 22, padding: '20px 26px',
  boxShadow: '0 10px 30px rgba(157, 23, 77, 0.3)',
  textAlign: 'center', minWidth: 220,
  animation: 'pop 360ms cubic-bezier(.2,.9,.3,1.4)',
};

function cornerToastStyle(bg: string, fg: string): React.CSSProperties {
  return {
    position: 'fixed',
    bottom: 92, right: 14,
    background: bg, color: fg,
    padding: '10px 14px', borderRadius: 14,
    fontWeight: 800, fontSize: 16,
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
    pointerEvents: 'none', zIndex: 9999,
    animation: 'slideUp 220ms ease-out',
  };
}

// Sparkle dots that fly outward from center for the level-up moment.
function Sparks() {
  const dots = Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * Math.PI * 2;
    const dist = 90 + (i % 3) * 18;
    return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, delay: i * 35 };
  });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {dots.map((d, i) => (
        <div
          key={i}
          style={{
            position: 'absolute', left: '50%', top: '50%',
            width: 12, height: 12, marginLeft: -6, marginTop: -6,
            background: i % 2 ? '#f9a8d4' : '#fde68a',
            borderRadius: '50%',
            animation: `spark 900ms ease-out ${d.delay}ms forwards`,
            // CSS vars for the keyframe target
            ['--tx' as never]: `${d.x}px`,
            ['--ty' as never]: `${d.y}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes spark {
          0%   { transform: translate(0, 0) scale(0.5); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(var(--tx), var(--ty)) scale(0.2); opacity: 0; }
        }
        @keyframes pop {
          0%   { transform: scale(0.6); opacity: 0; }
          70%  { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1.0); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
